import InputLabelField from '@/components/form-control/InputLabelField'
import { useAuthContext } from '@/features/auth/context'

import { useConfigUI } from '@/features/config/context'
import { useAddPlant, useUpdatePlant } from '@/features/config/hooks/plants'
import usePlantById from '@/features/config/hooks/plants/usePlantById'
import { PLANT_NAME_MAX_LENGTH } from '@/constant/common'
import useTranslation from '@/hooks/useTranslation'
import { useUnits } from '@/hooks/useUnits'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Container,
  Fade,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import useUser from '@features/auth/hooks/useUser'

interface PlantViewProps {
  id?: string
}

const schema = yup
  .object({
    description: yup
      .string()
      .trim()
      .required('Plant Description is required')
      .max(
        PLANT_NAME_MAX_LENGTH,
        `Maximum ${PLANT_NAME_MAX_LENGTH} characters`,
      ),
    allowableDepletion: yup
      .number()
      // .integer('Allowable Depletion is an integer')
      .transform((value, originalValue) => {
        return originalValue === '' ? undefined : value
      })
      .typeError(' Allowable Depletion is a number')
      // .required('Allowable Depletion is required')
      .min(0, 'Minimum 0%')
      .max(100, 'Maximum 100%'),
    refillTo: yup
      .number()
      // .integer('Refill To is an integer')
      .transform((value, originalValue) => {
        return originalValue === '' ? undefined : value
      })
      .typeError('Refill To is a number')
      // .required('Refill To is required')
      .min(0, 'Minimum 0%')
      .max(100, 'Maximum 100%'),
    rootZone: yup
      .number()
      // .integer('Root Zone is an integer')
      .transform((value, originalValue) => {
        return originalValue === '' ? undefined : value
      })
      .typeError('Root Zone is a number')
      // .required('Root Zone is required')
      .min(0, 'Minimum 0'),
    factor: yup
      .number()
      // .integer('Plant Factor is an integer')
      .transform((value, originalValue) => {
        return originalValue === '' ? undefined : value
      })
      .typeError('Plant Factor is a number')
      // .required('Plant Factor is required')
      .min(0, 'Minimum 0%')
      .max(100, 'Maximum 100%'),
  })
  .required()

const PlantView = ({ id }: PlantViewProps) => {
  const { t } = useTranslation()
  const { closeSidebar } = useConfigUI()
  const { interfaceId } = useAuthContext()

  const { convertFromMetricV2 } = useUnits()

  const { mutate: addPlant, isLoading: loadingAddPlant } = useAddPlant()

  const { mutate: editPlant, isLoading: loadingEditPlant } = useUpdatePlant()

  const inputRefSiteName = useRef<HTMLInputElement>(null)

  const { data: editItem, isLoading: isLoadingPlant } = usePlantById(
    interfaceId,
    id,
  )
  const { data: user } = useUser()

  const mode = id ? 'edit' : 'create'
  const isLoading = loadingAddPlant || loadingEditPlant

  const defaultFormValues = useMemo(
    () => ({
      id: '',
      description: '',
    }),
    [],
  )

  const form = useForm({
    defaultValues: {
      ...defaultFormValues,
      ...editItem,
    },
    resolver: yupResolver(schema),
  })

  const handleSave = (data: any) => {
    if (mode === 'create') {
      addPlant({
        interfaceId: Number(interfaceId),
        description: data.description,
      })
    }

    if (mode === 'edit') {
      editPlant({
        id: Number(id),
        interfaceId: Number(interfaceId),
        description: data.description,
      })
    }
  }

  const convertedRootZone = useMemo(() => {
    if (!editItem?.rootZone) return 0
    return convertFromMetricV2(editItem.rootZone, 'mm', 'in', 2)
  }, [editItem?.rootZone, convertFromMetricV2])

  useEffect(() => {
    if (inputRefSiteName.current) {
      inputRefSiteName.current.focus()
    }
  }, [])

  useEffect(() => {
    if (editItem?.id) {
      form.reset({
        id: editItem.id,
        description: editItem.description,
      })
    } else {
      form.reset(defaultFormValues)
    }
  }, [convertedRootZone, defaultFormValues, editItem, form])

  return (
    <Container sx={{ maxWidth: '450px', height: '100vh' }}>
      {isLoadingPlant && (
        <Stack
          width="377px"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      )}
      {!isLoadingPlant && (
        <Fade in={!isLoadingPlant}>
          <form onSubmit={form.handleSubmit(handleSave)}>
            <Stack sx={{ padding: '34px 18px' }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '377px', marginBottom: '20px' }}
              >
                <Typography color="text.main">
                  {id ? t('dataConfigEditPlant') : t('dataConfigAddPlant')}
                </Typography>
                <Stack direction="row">
                  <Button
                    variant="secondary"
                    size="small"
                    sx={{ marginRight: '7px', width: '85px' }}
                    onClick={() => closeSidebar()}
                  >
                    {t('commonActionsCancel')}
                  </Button>
                  <LoadingButton
                    loading={isLoading}
                    size="small"
                    variant="primary"
                    sx={{ minWidth: '72px' }}
                    type="submit"
                  >
                    {t('commonActionsSave')}
                  </LoadingButton>
                </Stack>
              </Stack>

              <InputLabelField
                isRequired
                name="description"
                form={form}
                maxLength={PLANT_NAME_MAX_LENGTH}
                label={t('plantDescription')}
                placeholder={t('plantDescription')}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  width: { xs: '377px' },
                  marginTop: '8px',
                }}
                padding="8px 16px"
              />

            </Stack>
          </form>
        </Fade>
      )}
    </Container>
  )
}

export default PlantView
